import { Accordion, Group, SimpleGrid, Stack, Text } from '@mantine/core'
import type { AttachmentResponse, VisitWithRelations } from '@markku/types'
import { IconCheck, IconPhoto } from '@tabler/icons-react'
import { Trans, useLingui } from '@lingui/react/macro'

import { PhotoGallery } from '@markku/components'

interface TaskAccordionItemProps {
  task: NonNullable<VisitWithRelations['visitTasks']>[number]
  taskAttachments: AttachmentResponse[]
  index: number
}

export function TaskAccordionItem({
  task,
  taskAttachments,
  index,
}: TaskAccordionItemProps) {
  const { t } = useLingui()
  const taskName =
    task.taskName || task.maintenanceTask?.name || t`Task ${index + 1}`
  const taskDescription =
    task.taskDescription || task.maintenanceTask?.description

  const beforePhotos = taskAttachments.filter(
    (p) => p.metadata?.type === 'before',
  )
  const afterPhotos = taskAttachments.filter(
    (p) => p.metadata?.type === 'after',
  )

  return (
    <Accordion.Item key={task.id} value={`task-${task.id}`}>
      <Accordion.Control>
        <Group align="flex-start" wrap="nowrap">
          <IconCheck
            size={20}
            color="var(--mantine-color-green-6)"
            style={{ flexShrink: 0, marginTop: 2 }}
          />
          <Stack gap={4} style={{ flex: 1 }}>
            <Text fw={500}>{taskName}</Text>
            <Group gap="sm">
              {taskAttachments.length > 0 && (
                <Group gap={4}>
                  <IconPhoto size={14} color="var(--mantine-color-gray-6)" />
                  <Text size="xs" c="dimmed">
                    <Trans>{taskAttachments.length} photos</Trans>
                  </Text>
                </Group>
              )}
            </Group>
          </Stack>
        </Group>
      </Accordion.Control>

      <Accordion.Panel>
        <Stack gap="md">
          {taskDescription && (
            <Text size="sm" c="dimmed">
              {taskDescription}
            </Text>
          )}

          {task.notes && (
            <>
              <Text size="sm" fw={500}>
                <Trans>Markku's notes</Trans>
              </Text>
              <Text size="sm" c="dimmed">
                {task.notes}
              </Text>
            </>
          )}

          {taskAttachments.length > 0 && (
            <>
              {beforePhotos.length === 1 && afterPhotos.length === 1 ? (
                // Single before/after pair - side by side for direct comparison
                <SimpleGrid cols={2} spacing="md">
                  <PhotoGallery
                    photos={beforePhotos}
                    label={t`Before`}
                    cols={1}
                  />
                  <PhotoGallery
                    photos={afterPhotos}
                    label={t`After`}
                    cols={1}
                  />
                </SimpleGrid>
              ) : (
                // Multiple photos - stack for more space
                <Stack gap="md">
                  <PhotoGallery photos={beforePhotos} label={t`Before`} />
                  <PhotoGallery photos={afterPhotos} label={t`After`} />
                </Stack>
              )}
            </>
          )}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  )
}
